import { Box } from '@mui/material';
import { useMemo, useState } from 'react';
import Feedback from '../special/Feedback';
import { API_POST, API_PATCH, DBRequest, useConfig } from '../../api/api';
import * as Yup from 'yup';
import ExoForm from '../exo/ExoForm';
import { useTranslation } from 'react-i18next';
import Loader from '../special/Loader';
import useFetch from '../../api/useFetch';

const DEFAULT_TIME_WINDOWS = {
  monday: [{ start: '08:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  tuesday: [{ start: '08:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  wednesday: [{ start: '08:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  thursday: [{ start: '08:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  friday: [{ start: '08:00', end: '14:00' }],
  saturday: null,
  sunday: null,
};

const NewBookingPage = ({
  onClose,
  onCreate = () => {},
  startData,
  fullWidth,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const [isLoading, setIsLoading] = useState(false);

  const { apiData: calendarsData } = useFetch('calendar/own?noEvents=true');

  const calendarOptions = useMemo(() => {
    if (!calendarsData?.data) return [];
    return calendarsData.data
      .filter((cal) => !cal.isVirtual)
      .map((cal) => ({
        label: cal.name,
        value: cal.id,
      }));
  }, [calendarsData]);

  const fields = [
    {
      label: t('Name'),
      type: 'text',
      key: 'name',
    },
    {
      label: `${t('Description')} (${t('optional')})`,
      type: 'richText',
      key: 'description',
    },
    {
      label: t('Target Calendar'),
      type: 'customSelect',
      key: 'targetCalendarId',
      options: calendarOptions,
    },
    {
      label: t('Categories'),
      type: 'bookingCategories',
      key: 'categories',
    },
    {
      label: t('Time Windows'),
      type: 'timeWindows',
      key: 'timeWindows',
    },
    {
      label: `${t('Appointment Duration')} (${t('Minutes')})`,
      type: 'number',
      key: 'appointmentDuration',
    },
    {
      label: `${t('Buffer Before')} (${t('Minutes')})`,
      type: 'number',
      key: 'bufferBefore',
    },
    {
      label: `${t('Buffer After')} (${t('Minutes')})`,
      type: 'number',
      key: 'bufferAfter',
    },
    {
      label: t('Advance Days'),
      type: 'number',
      key: 'advanceDays',
    },
    {
      label: `${t('Min. Notice Hours')}`,
      type: 'number',
      key: 'minNoticeHours',
    },
    {
      label: t('Online Meeting'),
      type: 'checkbox',
      key: 'offerOnline',
    },
    {
      label: t('Locations'),
      type: 'bookingLocations',
      key: 'locations',
    },
    {
      label: t('Allowed Origins'),
      type: 'nameValueArray',
      key: 'allowedOrigins',
      addLabel: t('Add Origin'),
      nameKey: 'name',
      valueKey: 'value',
      placeholder: {
        name: t('Label'),
        value: 'https://example.com',
      },
      radioItems: [],
    },
    {
      label: t('Reminders'),
      type: 'reminder',
      key: 'reminderOffsets',
      relativeOnly: true,
    },
    {
      // Passed to the GTM dataLayer as booking.conversionKey on a completed
      // booking. GTM owns the actual Google Ads conversion (ID/label/trigger);
      // this is just an optional key GTM can match on to differentiate pages.
      label: `${t('GTM Conversion Key')} (${t('optional')})`,
      type: 'text',
      key: 'gtagConversion',
      placeholder: 'z.B. erstgespraech',
    },
    {
      label: t('Active'),
      type: 'checkbox',
      key: 'isActive',
    },
  ];

  function handleSubmit(data) {
    // Transform allowedOrigins from name-value array to simple string array
    // Transform reminderOffsets to only send type + value (strip remindAt, uuid)
    const submitData = {
      ...data,
      allowedOrigins: data.allowedOrigins
        ? data.allowedOrigins.map((item) => item.value || item)
        : [],
      reminderOffsets: data.reminderOffsets
        ? data.reminderOffsets.map(({ type, value }) => ({ type, value }))
        : [],
    };

    DBRequest({
      config,
      path: submitData.id
        ? `booking-pages/${submitData.id}`
        : 'booking-pages',
      method: submitData.id ? API_PATCH(submitData) : API_POST(submitData),
      onResponse: handleData,
      onFeedback: handleFeedback,
      onLoading: setIsLoading,
    });
  }

  function handleData(data) {
    onCreate(data.data);
    onClose();
  }

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });

  function handleFeedback(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  // FIX 1: Memoize startDataCollection to prevent ExoForm's useEffect
  // from resetting user data on parent re-renders (e.g. when calendarsData loads)
  const startDataCollection = useMemo(() => {
    if (startData) {
      // FIX 4: Transform allowedOrigins from ["url"] to [{name, value}] for nameValueArray field
      return {
        ...startData,
        allowedOrigins: startData.allowedOrigins
          ? startData.allowedOrigins.map((origin) =>
              typeof origin === 'string'
                ? { name: origin, value: origin }
                : origin
            )
          : [],
      };
    }
    return {
      timeWindows: DEFAULT_TIME_WINDOWS,
      appointmentDuration: 30,
      bufferBefore: 15,
      bufferAfter: 15,
      advanceDays: 60,
      minNoticeHours: 24,
      isActive: true,
      offerOnline: false,
      locations: [],
      allowedOrigins: [],
      reminderOffsets: [],
      categories: [],
    };
  }, [startData]);

  const isEditing = Boolean(startData?.id);

  return (
    <Box
      className="p-2 h-full overflow-y-auto"
      sx={{ width: fullWidth ? '100%' : '700px', maxWidth: '100%' }}
    >
      <Loader active={isLoading} />
      <ExoForm
        draftType="booking-page"
        startDataCollection={startDataCollection}
        fields={fields}
        onCancel={onClose}
        header={isEditing ? t('Edit Booking Page') : t('New Booking Page')}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        // FIX 2: New entries must send ALL data (noDifferences=true),
        // edits send only changed fields (noDifferences=false)
        noDifferences={!isEditing}
      />
      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};

// FIX 3: Frontend validation matching backend StoreBookingPageRequest rules
const validationSchema = Yup.object().shape({
  name: Yup.string().max(255).required('Name is required'),
  targetCalendarId: Yup.number()
    .typeError('Target calendar is required')
    .required('Target calendar is required'),
  categories: Yup.array()
    .min(1, 'At least one category is required')
    .required('Categories are required'),
  timeWindows: Yup.object().required('Time windows are required'),
  appointmentDuration: Yup.number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(5, 'Minimum 5 minutes')
    .max(480, 'Maximum 480 minutes')
    .required('Duration is required'),
  bufferBefore: Yup.number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(0, 'Minimum 0 minutes')
    .max(120, 'Maximum 120 minutes'),
  bufferAfter: Yup.number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(0, 'Minimum 0 minutes')
    .max(120, 'Maximum 120 minutes'),
  advanceDays: Yup.number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(1, 'Minimum 1 day')
    .max(365, 'Maximum 365 days'),
  minNoticeHours: Yup.number()
    .typeError('Must be a number')
    .integer('Must be a whole number')
    .min(0, 'Minimum 0 hours')
    .max(168, 'Maximum 168 hours'),
});

export default NewBookingPage;
